import RichText from '@/components/RichText'
import { cn } from '@/utilities/ui'

import type { TableBlock as TableBlockFields } from '@/payload-types'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

export type TableBlockProps = Pick<TableBlockFields, 'title' | 'description' | 'headers' | 'rows'>

export const TableBlock: React.FC<TableBlockProps> = ({ title, description, headers, rows }) => {
  const headerList = headers ?? []
  const lastHeaderIdx = headerList.length - 1
  const lastRowIdx = rows.length - 1
  const singleCol = headerList.length === 1

  return (
    <div className="col-start-2 mx-auto my-4 w-full max-w-4xl">
      {title && (
        <div className="mb-4 flex flex-col gap-4 text-center">
          <h2 className="text-2xl font-bold">{title}</h2>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr className="text-foreground">
              {headerList.map((header, idx) => (
                <th
                  key={header.id ?? `header-${idx}`}
                  className={cn(
                    'border-y border-r border-gray-500 bg-sage-200 px-4 py-2',
                    idx === 0 && 'border-l',
                    singleCol && 'rounded-t-xl',
                    !singleCol && idx === 0 && 'rounded-tl-xl',
                    !singleCol && idx === lastHeaderIdx && 'rounded-tr-xl',
                  )}
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              const cells = row.cells ?? []
              const isLastRow = rowIdx === lastRowIdx
              const lastCellIdx = cells.length - 1
              return (
                <tr key={row.id ?? `row-${rowIdx}`} className="text-foreground">
                  {cells.map((cell, cellIdx) => (
                    <td
                      key={cell.id ?? `${rowIdx}-${cellIdx}`}
                      className={cn(
                        'border-r border-b border-gray-500 bg-card px-4 py-2',
                        cellIdx === 0 && 'border-l',
                        isLastRow && singleCol && 'rounded-b-xl',
                        isLastRow && !singleCol && cellIdx === 0 && 'rounded-bl-xl',
                        isLastRow && !singleCol && cellIdx === lastCellIdx && 'rounded-br-xl',
                      )}
                    >
                      {cell.value}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {description && (
        <div className="mt-4 flex w-full justify-center">
          <RichText
            data={description as DefaultTypedEditorState}
            className="w-full max-w-2xl text-center text-sm text-muted-foreground [&_p]:text-center"
            enableGutter={false}
          />
        </div>
      )}
    </div>
  )
}
